import { Client, Conversation, Message, Paginator } from '@twilio/conversations';
import { Dispatch } from 'react';
import { getPreviousMessages } from 'services/chatService';
import {
  ADD_MESSAGE,
  GET_CONVERSATION_FAILURE,
  GET_CONVERSATIONS_REQUEST,
  GET_CONVERSATIONS_SUCCESS,
  GET_MESSAGES_FAILURE,
  GET_MESSAGES_REQUEST,
  GET_MESSAGES_SUCCESS,
  SEND_MESSAGE_FAILURE,
  SEND_MESSAGE_REQUEST,
  SEND_MESSAGE_SUCCESS,
  SELECT_CONVERSATION,
  APPEND_PREVIOUS_MESSAGE_REQUEST,
  APPEND_PREVIOUS_MESSAGE_FAILURE,
  APPEND_PREVIOUS_MESSAGE_SUCCESS,
  GET_UNREAD_MESSAGES_COUNT_REQUEST,
  GET_UNREAD_MESSAGES_COUNT_SUCCESS,
  GET_UNREAD_MESSAGES_COUNT_FAILURE,
  SET_ALL_MESSAGES_READ_REQUEST,
  SET_ALL_MESSAGES_READ_SUCCESS,
  SET_ALL_MESSAGES_READ_FAILURE,
} from 'state/actionTypes/chatActionTypes';
import {
  ChatAction,
  ConversationMessages,
} from 'state/contexts/ChatContext/ChatContext.types';

export const getConversations = async (
  dispatch: Dispatch<ChatAction>,
  client: Client,
): Promise<Conversation[] | undefined> => {
  try {
    dispatch({ type: GET_CONVERSATIONS_REQUEST });

    const conversations = (await client.getSubscribedConversations()).items;
    dispatch({
      type: GET_CONVERSATIONS_SUCCESS,
      payload: conversations,
    });

    return conversations;
  } catch (err) {
    dispatch({
      type: GET_CONVERSATION_FAILURE,
      payload: `Conversation error: ${(err as Error).message}`,
    });
  }
};

export const getMessages = async (
  dispatch: Dispatch<ChatAction>,
  conversations: Conversation[],
): Promise<void> => {
  try {
    dispatch({ type: GET_MESSAGES_REQUEST });

    const messagesP = conversations.map(async (conversation) => {
      const conversationMessagePaginator = await conversation.getMessages(100);
      const unreadMessagesCount = await conversation.getUnreadMessagesCount();

      return {
        sid: conversation.sid,
        conversationMessages: conversationMessagePaginator.items,
        conversationMessagePaginator,
        unreadMessagesCount,
        conversation,
      };
    });

    const promiseAllMessages = Promise.all(messagesP).then((values) => {
      const conversationMessages = values.reduce(
        (conversationsData, currentConversationData) => {
          return {
            ...conversationsData,
            [currentConversationData.sid]: {
              messages: currentConversationData.conversationMessages,
              unreadMessagesCount: currentConversationData.unreadMessagesCount || 0,
              paginator: currentConversationData.conversationMessagePaginator,
              conversation: currentConversationData.conversation,
            },
          };
        },
        {} as ConversationMessages,
      );

      return { conversationMessages };
    });

    promiseAllMessages.then(({ conversationMessages }) => {
      dispatch({
        type: GET_MESSAGES_SUCCESS,
        payload: {
          conversationMessages,
        },
      });
    });
  } catch (err) {
    dispatch({
      type: GET_MESSAGES_FAILURE,
      payload: (err as Error).message,
    });
  }
};

export const sendMessage = async (
  dispatch: Dispatch<ChatAction>,
  conversation: Conversation,
  message: string,
): Promise<void> => {
  dispatch({
    type: SEND_MESSAGE_REQUEST,
  });
  try {
    conversation.sendMessage(message);
    dispatch({
      type: SEND_MESSAGE_SUCCESS,
      payload: message,
    });
  } catch (err) {
    dispatch({
      type: SEND_MESSAGE_FAILURE,
      payload: (err as Error).message,
    });
  }
};

export const addMessage = (
  dispatch: Dispatch<ChatAction>,
  payload: { message: Message; conversationSid: string },
): void => {
  dispatch({
    type: ADD_MESSAGE,
    payload,
  });
};

export const selectConversationAction = (
  dispatch: Dispatch<ChatAction>,
  payload: string,
): void => {
  dispatch({
    type: SELECT_CONVERSATION,
    payload,
  });
};

export const appendPreviousMessagesAction = async (
  dispatch: Dispatch<ChatAction>,
  messagesPaginator: Paginator<Message>,
  conversationSid: string,
): Promise<void> => {
  if (!messagesPaginator.hasPrevPage) return;
  dispatch({
    type: APPEND_PREVIOUS_MESSAGE_REQUEST,
  });
  try {
    const paginator = await getPreviousMessages(messagesPaginator);
    if (paginator) {
      dispatch({
        type: APPEND_PREVIOUS_MESSAGE_SUCCESS,
        payload: {
          paginator,
          sid: conversationSid,
        },
      });
    }
  } catch (err) {
    dispatch({
      type: APPEND_PREVIOUS_MESSAGE_FAILURE,
      payload: (err as Error).message,
    });
  }
};

export const getUnreadMessagesCountAction = async (
  dispatch: Dispatch<ChatAction>,
  conversation: Conversation,
): Promise<void> => {
  dispatch({
    type: GET_UNREAD_MESSAGES_COUNT_REQUEST,
  });
  try {
    const unreadMessagesCount = await conversation.getUnreadMessagesCount();

    dispatch({
      type: GET_UNREAD_MESSAGES_COUNT_SUCCESS,
      payload: {
        unreadMessagesCount: unreadMessagesCount || 0,
        sid: conversation.sid,
      },
    });
  } catch (err) {
    dispatch({
      type: GET_UNREAD_MESSAGES_COUNT_FAILURE,
      payload: (err as Error).message,
    });
  }
};

export const setAllMessagesReadAction = async (
  dispatch: Dispatch<ChatAction>,
  conversation: Conversation,
): Promise<void> => {
  dispatch({
    type: SET_ALL_MESSAGES_READ_REQUEST,
  });
  try {
    await conversation.setAllMessagesRead();

    dispatch({
      type: SET_ALL_MESSAGES_READ_SUCCESS,
      payload: conversation.sid,
    });
  } catch (err) {
    dispatch({
      type: SET_ALL_MESSAGES_READ_FAILURE,
      payload: (err as Error).message,
    });
  }
};
